import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Upload, FileCheck2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { parseResumeAnonymous } from '../lib/api';

const ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.heic,.heif';
const MAX_SIZE_MB = 10;

/**
 * Resume upload card for the UNAUTHENTICATED manual signup flow (Auth.js).
 *
 * Differs from ResumeUploadCard:
 *   - Calls /api/parse-resume-anonymous (no auth, no storage)
 *   - File is held in component state and reported via onFileSelected so the
 *     parent (Auth.js) can re-upload it AFTER the account is created if the
 *     user wants the file saved permanently.
 *
 * Props:
 *   onParsed         fn(extracted)        called with the extracted JSON
 *   onFileSelected   fn(File)             optional — receives the original File so
 *                                         parent can save & re-upload after signup
 */
export function AnonymousResumeUploadCard({ onParsed, onFileSelected }) {
  const { t } = useTranslation();
  const [parsing, setParsing] = useState(false);
  const [parsedFilename, setParsedFilename] = useState(null);
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.info(t('resume.tooLarge', `File too large. Max ${MAX_SIZE_MB} MB.`));
      return;
    }

    setParsing(true);
    try {
      const extracted = await parseResumeAnonymous(file);
      const filledFields = Object.entries(extracted)
        .filter(([k]) => !k.startsWith('_'))
        .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));

      if (filledFields.length === 0) {
        toast.info(t('resume.couldNotRead', "We couldn't read this resume. You can try a different file or fill in your profile below."));
        return;
      }

      setParsedFilename(file.name);
      if (typeof onFileSelected === 'function') onFileSelected(file);
      if (typeof onParsed === 'function') onParsed(extracted);
      toast.success(t('resume.signupParsed', 'Resume read! We pre-filled the form below — review and adjust before signing up.'));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        // 429 can be the per-IP burst/daily limit OR the global daily cap. Prefer the
        // server's own message (it explains which and what to do — e.g. "sign up below")
        // instead of a hardcoded, often-wrong "you've used your 3 parses" line.
        const serverDetail = err?.response?.data?.detail;
        toast.info(serverDetail || t('resume.rateLimited', "The free resume reader is busy right now. Please try again later, or fill in your profile below."));
      } else if (status === 422 || status === 415 || status === 413 || status === 400) {
        toast.info(t('resume.couldNotRead', "We couldn't read this resume. You can try a different file or fill in your profile below."));
      } else {
        toast.info(t('resume.parseFailed', "We couldn't process the resume right now. Try again in a moment."));
      }
      console.error('Anonymous resume parse failed:', err);
    } finally {
      setParsing(false);
    }
  };

  return (
    <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
      <CardContent className="p-5">
        {parsedFilename ? (
          <div className="flex items-center gap-3">
            <FileCheck2 className="w-5 h-5 text-success flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{parsedFilename}</p>
              <p className="text-xs text-muted-foreground">
                {t('resume.signupParsedHint', "Form pre-filled below. You can re-pick to try a different resume.")}
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={handleFileChange}
              className="hidden"
              disabled={parsing}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={parsing}
              onClick={() => inputRef.current?.click()}
            >
              {parsing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.parsing', 'Reading your resume...')}</>
              ) : (
                t('resume.replace', 'Replace')
              )}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <p className="font-semibold">
                {t('resume.signupTitle', 'Have a resume? Skip the typing.')}
              </p>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('resume.signupSubtitle', "Upload your resume and we'll pre-fill the form below. You can review and adjust everything before signing up.")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('resume.acceptFormats', 'PDF, DOCX, JPG, PNG, HEIC up to 10 MB')}
            </p>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={handleFileChange}
              className="hidden"
              disabled={parsing}
            />
            <Button
              type="button"
              variant="outline"
              disabled={parsing}
              onClick={() => inputRef.current?.click()}
            >
              {parsing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.parsing', 'Reading your resume...')}</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> {t('resume.uploadAndFill', 'Upload resume to fill the form')}</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AnonymousResumeUploadCard;
